/**
 * RecentPerformancesContainer - Container for recent match performances
 *
 * Fetches Gemini Game Ratings (GGR) via listGameMetrics and falls back to
 * OBV-based ratings from listGameProviderMetrics when GGR data is unavailable.
 * Correlates metrics with player matches by gameId for opponent/date context.
 * When GGR is the primary rating source, actual playing minutes are sourced
 * from OBV's minutesTotal rather than hardcoded estimates. The "Last 1000 min"
 * filter excludes DNP matches (0 minutes) to show only actual playing time.
 * @remarks This is a Container component - handles all business logic
 */

import { useCallback, useMemo, useState } from "react";

import RecentPerformancesView from "./RecentPerformancesView";

import type {
  MatchPerformance,
  MatchStatus,
  PerformanceFilter,
} from "@/features/player-detail/types";

import {
  PlayerDetailPlayerMetaFragment,
  usePlayerDetailListGameMetricsQuery,
  usePlayerDetailListGameProviderMetricsQuery,
  usePlayerDetailListPlayerMatchesQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import { useAppLocale } from "@/i18n";
import {
  MONTH_DAY_OPTIONS,
  formatDate as formatIntlDate,
} from "@/utils/intlFormat";

/**
 * Props for RecentPerformancesContainer
 */
interface RecentPerformancesContainerProps {
  /** Player data from parent query */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether dark mode is active */
  readonly isDark?: boolean;
}

/** Maximum matches to display */
const MAX_DISPLAY_MATCHES = 10;

/** Minutes threshold to consider player as starter (OBV fallback) */
const STARTER_MINUTES_THRESHOLD = 60;

/** Estimated minutes for started matches (used in last1000min filter with GGR) */
const ESTIMATED_STARTER_MINUTES = 85;

/** Estimated minutes for substitute appearances (used in last1000min filter with GGR) */
const ESTIMATED_SUB_MINUTES = 25;

/**
 * Derive match status from Gemini Game Metrics boolean fields.
 * A player is "sub" if they came off the bench (wasSubbedIn) OR started but
 * were removed early (wasSubbedOut without playedEnoughMinutes).
 * @param wasSubbedIn - Whether the player entered as a substitute
 * @param playedEnoughMinutes - Whether the player met the minutes threshold
 * @param wasSubbedOut - Whether the player was substituted off during the match
 */
function deriveMatchStatusFromGgr(
  wasSubbedIn: boolean | null | undefined,
  playedEnoughMinutes: boolean | null | undefined,
  wasSubbedOut: boolean | null | undefined
): MatchStatus {
  if (wasSubbedIn) return "sub";
  if (playedEnoughMinutes) return "started";
  if (wasSubbedOut) return "sub";
  return "dnp";
}

/**
 * Derive match status from minutes played (OBV fallback)
 * @param minutes - Total minutes played in the match
 */
function deriveMatchStatusFromMinutes(
  minutes: number | null | undefined
): MatchStatus {
  const mins = minutes ?? 0;
  if (mins >= STARTER_MINUTES_THRESHOLD) return "started";
  if (mins > 0) return "sub";
  return "dnp";
}

/**
 * Estimate minutes played from match status for the last1000min filter.
 * @param status - Derived match status
 */
function estimateMinutesFromStatus(status: MatchStatus): number {
  if (status === "started") return ESTIMATED_STARTER_MINUTES;
  if (status === "sub") return ESTIMATED_SUB_MINUTES;
  return 0;
}

/**
 * Compute rating from On-Ball Value (OBV) — used when GGR is unavailable.
 * Normalizes OBV to a 1-5 scale based on typical distribution.
 * @param obv - On-Ball Value total for the match
 */
function computeRatingFromObv(obv: number | null | undefined): number {
  const obvValue = obv ?? 0;
  const normalized = Math.max(1, Math.min(5, (obvValue + 1) * 2 + 1));
  return Math.round(normalized * 10) / 10;
}

/**
 * Format date with abbreviated year (e.g., "Jan 25 '26")
 *
 * SE-7966 makes the month/day part follow the account language — that part is
 * a locale decision and was hardcoded "en-US". The `'YY` suffix is left as-is
 * deliberately: an apostrophe-elided year is an English typographic idiom, not
 * something `Intl` expresses, so localising it would mean inventing a
 * convention for five other languages. The column's shape is unchanged.
 * @param dateString - ISO date string
 * @param locale - The active UI locale
 */
function formatMatchDate(
  dateString: string | null | undefined,
  locale: string
): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const monthDay = formatIntlDate(dateString, locale, MONTH_DAY_OPTIONS);
  if (!monthDay) return "—";
  const year = date.getFullYear().toString().slice(2);
  return `${monthDay} '${year}`;
}

/**
 * RecentPerformancesContainer handles data fetching and filter logic
 * @param props - Container props
 * @param props.player - Player data from parent query
 * @param props.isDark - Whether dark mode is active
 */
const RecentPerformancesContainer = ({
  player,
  isDark: propIsDark,
}: RecentPerformancesContainerProps) => {
  const locale = useAppLocale();
  // 1. VARIABLES & DERIVED STATE
  const [activeFilter, setActiveFilter] = useState<PerformanceFilter>("last10");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const { resolvedTheme } = useTheme();
  const themeIsDark = resolvedTheme === "dark";
  const isDark = propIsDark ?? themeIsDark;
  const isSmallScreen = useMobileScreen();

  const playerId = player?.id;

  // Fetch Gemini Game Ratings (preferred)
  const {
    data: ggrData,
    loading: ggrLoading,
    error: ggrError,
  } = usePlayerDetailListGameMetricsQuery({
    variables: { playerId: playerId ?? "" },
    skip: !playerId,
  });

  // Fetch OBV-based game metrics (fallback when GGR is empty)
  const {
    data: obvData,
    loading: obvLoading,
    error: obvError,
  } = usePlayerDetailListGameProviderMetricsQuery({
    variables: { playerId: playerId ?? "" },
    skip: !playerId,
  });

  // Fetch player matches for context (opponent, date, etc.)
  const {
    data: matchesData,
    loading: matchesLoading,
    error: matchesError,
  } = usePlayerDetailListPlayerMatchesQuery({
    variables: { playerId: playerId ?? "", first: 50 },
    skip: !playerId,
  });

  // Determine which metrics source to use: GGR if available, else OBV
  const hasGgrData = (ggrData?.listGameMetrics?.length ?? 0) > 0;

  // DEBUG: Log query states
  if (__DEV__) {
    console.log("[RecentPerformances] Debug:", {
      playerId,
      ggrLoading,
      obvLoading,
      matchesLoading,
      ggrError: ggrError?.message,
      obvError: obvError?.message,
      matchesError: matchesError?.message,
      ggrCount: ggrData?.listGameMetrics?.length ?? 0,
      obvCount: obvData?.listGameProviderMetrics?.length ?? 0,
      matchesCount: matchesData?.listPlayerMatches?.edges?.length ?? 0,
      usingGgr: hasGgrData,
    });
  }

  // Create lookup maps for both metrics sources by gameId
  const ggrMap = useMemo(() => {
    const metrics = ggrData?.listGameMetrics ?? [];
    return metrics.reduce(
      (acc, m) => ({ ...acc, [m.gameId]: m }),
      {} as Record<string, (typeof metrics)[number]>
    );
  }, [ggrData?.listGameMetrics]);

  const obvMap = useMemo(() => {
    const metrics = obvData?.listGameProviderMetrics ?? [];
    return metrics.reduce(
      (acc, m) => ({ ...acc, [m.gameId]: m }),
      {} as Record<string, (typeof metrics)[number]>
    );
  }, [obvData?.listGameProviderMetrics]);

  // Combine matches with metrics to create performance data
  const allPerformances = useMemo((): readonly MatchPerformance[] => {
    const matches = matchesData?.listPlayerMatches?.edges ?? [];

    const result = matches.map(edge => {
      const match = edge.node;
      if (hasGgrData) {
        // Use Gemini Game Ratings — fall back to match.id for correlation
        const ggr = ggrMap[match.matchId] ?? ggrMap[match.id ?? ""];
        const status = deriveMatchStatusFromGgr(
          ggr?.wasSubbedIn,
          ggr?.playedEnoughMinutes,
          ggr?.wasSubbedOut
        );
        return {
          id: match.id ?? match.matchId,
          matchDate: match.matchDate ?? "",
          opponent: match.oppositionName ?? "Unknown",
          status,
          rating: ggr?.ggrRating ?? 0,
          minutes:
            obvMap[match.matchId]?.minutesTotal ??
            obvMap[match.id ?? ""]?.minutesTotal ??
            estimateMinutesFromStatus(status),
        };
      }

      // Fallback to OBV-based metrics — fall back to match.id for correlation
      const obv = obvMap[match.matchId] ?? obvMap[match.id ?? ""];
      return {
        id: match.id ?? match.matchId,
        matchDate: match.matchDate ?? "",
        opponent: match.oppositionName ?? "Unknown",
        status: deriveMatchStatusFromMinutes(obv?.minutesTotal),
        rating: computeRatingFromObv(obv?.obvTotalNetTotal),
        minutes: obv?.minutesTotal ?? 0,
      };
    });

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const futureMatches = result.filter(
      p => p.matchDate && new Date(p.matchDate).getTime() > today.getTime()
    );
    const pastMatches = result.filter(
      p => p.matchDate && new Date(p.matchDate).getTime() <= today.getTime()
    );

    if (__DEV__) {
      console.log("[RecentPerformances] today cutoff:", today.toISOString());
      console.log(
        "[RecentPerformances] total matches from query:",
        result.length
      );
      console.log(
        "[RecentPerformances] future matches excluded:",
        futureMatches.length,
        futureMatches.map(p => ({ date: p.matchDate, opponent: p.opponent }))
      );
      console.log(
        "[RecentPerformances] past matches kept:",
        pastMatches.length,
        pastMatches.slice(0, 5).map(p => ({
          date: p.matchDate,
          opponent: p.opponent,
          rating: p.rating,
          status: p.status,
        }))
      );
      console.log("[RecentPerformances] source:", hasGgrData ? "GGR" : "OBV");
    }

    return [...pastMatches].sort(
      (a, b) =>
        new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime()
    );
  }, [matchesData?.listPlayerMatches?.edges, hasGgrData, ggrMap, obvMap]);

  // Apply filter to performances
  const filteredPerformances = useMemo((): readonly MatchPerformance[] => {
    const now = new Date();
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    switch (activeFilter) {
      case "last10":
        return allPerformances.slice(0, MAX_DISPLAY_MATCHES);

      case "last1000min":
        return allPerformances
          .filter(p => p.minutes > 0)
          .slice(0, MAX_DISPLAY_MATCHES);

      case "last60days":
        return allPerformances
          .filter(p => new Date(p.matchDate) >= sixtyDaysAgo)
          .slice(0, MAX_DISPLAY_MATCHES);

      default:
        return allPerformances.slice(0, MAX_DISPLAY_MATCHES);
    }
  }, [activeFilter, allPerformances]);

  // Format performances for display
  const displayPerformances = useMemo(
    () =>
      filteredPerformances.map(p => ({
        ...p,
        formattedDate: formatMatchDate(p.matchDate, locale),
      })),
    [filteredPerformances, locale]
  );

  // Derive the selected performance from hoveredIndex (bars render reversed)
  const reversedPerformances = useMemo(
    () => [...displayPerformances].reverse(),
    [displayPerformances]
  );
  const selectedPerformance =
    hoveredIndex !== null ? (reversedPerformances[hoveredIndex] ?? null) : null;

  const isLoading = !player || ggrLoading || obvLoading || matchesLoading;
  const hasData = displayPerformances.length > 0;

  // Handle filter change
  const handleFilterChange = useCallback((filter: PerformanceFilter) => {
    setActiveFilter(filter);
  }, []);

  // Handle hover change (desktop pointer events)
  const handleHoverChange = useCallback((index: number | null) => {
    setHoveredIndex(index);
  }, []);

  // Handle bar press (mobile tap-to-toggle tooltip)
  const handlePressBar = useCallback((index: number) => {
    setHoveredIndex(prev => (prev === index ? null : index));
  }, []);

  // 2. RETURN
  return (
    <RecentPerformancesView
      isDark={isDark}
      isLoading={isLoading}
      hasData={hasData}
      performances={displayPerformances}
      activeFilter={activeFilter}
      onFilterChange={handleFilterChange}
      hoveredIndex={hoveredIndex}
      selectedPerformance={selectedPerformance}
      onHoverChange={handleHoverChange}
      onPressBar={handlePressBar}
      isSmallScreen={isSmallScreen}
    />
  );
};

RecentPerformancesContainer.displayName = "RecentPerformancesContainer";

export default RecentPerformancesContainer;
